import { Injectable, NgZone, inject } from '@angular/core';
import { LDClient, LDContext, initialize } from 'launchdarkly-js-client-sdk';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, take } from 'rxjs/operators';

import { SnackBarService } from '@mimecast-ui/ui-core/feedback';
import { WINDOW } from '@mimecast-ui/ui-core/window';

import {
  Flag,
  FlagsService,
  LAUNCH_DARKLY_CLIENT_ID,
  LAUNCH_DARKLY_CONTEXT,
  LaunchDarklyContext,
} from '../types/feature-flags.types';
import { defaultFlags } from './constants';

@Injectable()
export class FeatureFlagService implements FlagsService {
  private readonly clientId$: Observable<string> = inject(
    LAUNCH_DARKLY_CLIENT_ID
  );
  private readonly ngZone = inject(NgZone);
  private readonly snackBackService = inject(SnackBarService);
  private readonly window = inject(WINDOW);
  private readonly launchDarklyContext$: Observable<LaunchDarklyContext> =
    inject(LAUNCH_DARKLY_CONTEXT);

  private _ready = false;

  private launchDarklyClient?: LDClient;

  private fallBackFlagsStore: { [key: string]: unknown } = { ...defaultFlags };
  private failedToFetch = false;

  private readonly setup$ = forkJoin([
    this.launchDarklyContext$,
    this.clientId$,
  ]).pipe(
    mergeMap(([account, clientId]) => {
      const context = this.toLDContext(account);

      // Prevents invoking additional Change Detection cycles on LD requests (events)
      return this.ngZone.runOutsideAngular(() => {
        this.launchDarklyClient = initialize(clientId, context, {
          flushInterval: 5000,
        });

        return from(this.launchDarklyClient.waitForInitialization());
      });
    }),
    catchError((error: Error) => {
      /**
       * Failed to initialize launchDarkly client flow (i.e. HTTP request failed)
       */
      this.snackBackService.openAlert(
        {
          message: $localize`Failed to fetch configuration, some features may be unavailable at this time`,
          severity: 'error',
          actionType: 'close',
        },
        {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        }
      );
      this.failedToFetch = true;
      return of(true);
    }),
    map(() => {
      this._ready = true;
      return true;
    }),
    /** Prevent multiple subscriptions initialising another client */
    shareReplay(1)
  );

  initialize(): Observable<boolean> {
    this.setup$.subscribe();
    return this.setup$;
  }

  get ready() {
    return this._ready;
  }

  whenReady(): Observable<boolean> {
    return this.setup$;
  }

  getBooleanFlag(flag: string, defaultValue = false): boolean {
    return this.getFlag(flag, defaultValue);
  }

  getFlag<T>(flag: string, defaultValue: T): T {
    if (!this.launchDarklyClient || !this.ready || this.failedToFetch) {
      /**
       * Fallback in case launchDarkly wasn't able to load
       */
      if (flag in this.fallBackFlagsStore) {
        /**
         * If flag exists in fallbackStore - return value from it
         */
        return !!this.fallBackFlagsStore[flag] as T;
      } else {
        /**
         * Otherwise - create new record and return either a value from SessionStorage Overrides (set from the debugger dialog) if present
         * OR defaultValue argument
         */
        const override = this.getOverriddenFlag(flag, defaultValue);
        this.fallBackFlagsStore[flag] = override;
        return override;
      }
    }
    const flagState = this.launchDarklyClient.variation(flag, defaultValue);

    return this.getOverriddenFlag(flag, flagState);
  }

  getAllFlags() {
    if (this.launchDarklyClient && !this.failedToFetch) {
      return this.launchDarklyClient.allFlags();
    }

    /**
     * Return FallbackStore if wasn't able to initialize launchDarkly client
     */
    return this.fallBackFlagsStore;
  }

  private toLDContext({ account }: LaunchDarklyContext): LDContext {
    return {
      kind: 'multi',
      account: {
        kind: 'account',
        key: account.accountCode,
        code: account.accountCode,
        gridRegion: account.region,
        type: account.type,
      },
    };
  }

  private getOverriddenFlag<T>(flag: string, flagState: T): T {
    try {
      let overrides: { feature_flag: Record<string, T> } = { feature_flag: {} };
      const overriddenFlags =
        this.window.localStorage.getItem('debugger-overrides');

      if (overriddenFlags) {
        overrides = JSON.parse(overriddenFlags);
      }

      return overrides.feature_flag[flag] ?? flagState;
    } catch {
      return flagState;
    }
  }

  private _getBooleanFlag(flag: Flag, defaultValue?: boolean): boolean {
    let flagToCheck: string;
    const fallback = defaultValue ?? false;

    if (typeof flag === 'string') {
      flagToCheck = flag;
    } else {
      flagToCheck = flag.CG;
    }

    if (!flagToCheck) {
      return fallback;
    }

    return this.getBooleanFlag(flagToCheck, fallback);
  }

  getPlatformBooleanFlag$(
    flag: Flag,
    defaultValue?: boolean
  ): Observable<boolean> {
    return this.whenReady().pipe(
      take(1),
      map(() => {
        return this._getBooleanFlag(flag, defaultValue);
      })
    );
  }
}
